import { useNavigate, useParams } from 'react-router-dom';
import { PostStatus } from '../../../../ui/posts/constants';
import { isBlogger, isBloggerBusiness, isBusinessAdmin } from '../../../../utils/accessService';
import { getIsAuthenticated } from '../../../../utils/getIsAuthenticated';
import { usePostCollection } from '../../../post/usePostCollection';
import { useCollectionsController } from '../../../users/useCollectionsController';
import { useCurrentUser } from '../../../users/useCurrentUser';
import { useBusinessModel } from '../../useBusinessModel';
import { TabsValue } from '../constants';

const PER_PAGE = 24;

export const useBloggerTabRedirects = (tabsValue: TabsValue) => {
    const navigate = useNavigate();
    const currentUser = useCurrentUser();
    const isLoggedIn = getIsAuthenticated();
    const params = useParams();
    const businessId = params.businessId!;
    const business = useBusinessModel({ businessId });

    const isBloggerOwner =
        isBusinessAdmin(business?.id, currentUser) && isBlogger(currentUser?.role);

    const isUserDataReady = !isLoggedIn || currentUser;
    const shouldFetchCollections = Boolean(isUserDataReady && isBloggerBusiness(business?.type));

    const {
        savedCollections,
        collections,
        isLoaded: collectionsLoaded,
    } = useCollectionsController({
        businessId: isBloggerOwner ? undefined : business?.id,
        requestCondition: shouldFetchCollections,
    });

    const postCollection = usePostCollection({
        namespace: `business-overview-${businessId}`,
        filters: {
            statuses: [PostStatus.PUBLISHED, PostStatus.LIMITED],
            businessId,
            amount: PER_PAGE,
        },
        forceFiltersUpdate: true,
    });

    const hasSavedCollections = savedCollections.length > 0;
    const subcategoriesHidden = tabsValue === TabsValue.COLLECTIONS && !hasSavedCollections;

    if (!isBloggerBusiness(business?.type) || isBloggerOwner || !business) {
        return { tabsHidden: false, subcategoriesHidden, hasSavedCollections };
    }

    const emptyCollections = !collections.length;
    const emptyPosts = !postCollection.items.length;

    const tabsHidden = !isBloggerOwner && (emptyCollections || emptyPosts);

    const onPostsTab = tabsValue === TabsValue.POSTS;
    const onCollectionsTab = tabsValue === TabsValue.COLLECTIONS;

    // Проверяем, что данные загружены перед выполнением редиректов
    const dataLoaded = collectionsLoaded && postCollection.isLoaded;

    // редирект с пустого табика постов на коллеции
    if (dataLoaded && onPostsTab && emptyPosts && !emptyCollections) {
        navigate(`/profile/${business?.publicId}/collections`);
    }

    // редирект с пустого коллекций на посты
    if (dataLoaded && onCollectionsTab && emptyCollections && !emptyPosts) {
        navigate(`/profile/${business?.publicId}/`);
    }

    return { tabsHidden, subcategoriesHidden, hasSavedCollections };
};
